import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { Spinner } from '../feedback/spinner'
import { cn } from '../lib/cn'
import { glassSurface } from '../lib/surfaces'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-pill whitespace-nowrap font-display text-sm font-bold tracking-wide uppercase transition-colors focus-visible:outline-none focus-visible:focus-volee disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-card text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/85',
        // The page's ink on the page's ground, turned over: the quiet side of a bar that sits on the
        // ground, next to the lime way on.
        inverse: 'bg-foreground text-background hover:bg-foreground/90',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // `ghost`'s shape (no fill at rest) in `destructive`'s colour: a low-emphasis
        // action that is still a warning to read, not an emphasis to give it.
        danger: 'text-destructive hover:bg-destructive/10',
        // Dark, translucent and blurred: readable on the light page and on a photo alike.
        glass: `${glassSurface} hover:bg-background/70`,
        // `link` is text, not a button: it undoes the base's casing, because an uppercased
        // inline link reads as a broken button.
        link: 'text-primary-text normal-case underline-offset-4 hover:underline',
        // The secondary entry action keeps the body type, without uppercase.
        quiet:
          'font-body text-body tracking-normal text-foreground normal-case hover:text-primary-text',
      },
      /* `h-(--size-control-md)` and not `h-control-md`: in Tailwind 4 the short name
         emits no CSS and no error. Verified by compiling. */
      size: {
        default: 'h-(--size-control-md) px-4 py-2',
        sm: 'h-(--size-control-sm) px-3',
        lg: 'h-(--size-control-lg) px-8',
        // The entrance's own action: the biggest target, for the thumb.
        xl: 'h-(--size-control-xl) px-8 text-lg',
        icon: 'size-(--size-control-md)',
      },
      // Full width is what closes a section or a form in the wireframes, and five pages had
      // been writing `w-full` by hand.
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      block: false,
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Renders the child as the clickable element: this is what router link-buttons need. */
  asChild?: boolean
  /** In flight: disabled, `aria-busy`, spinner before the label. Ignored with `asChild` — Slot
   *  takes one child (packages/ui/docs/trappole.md § «Button loading non convive con asChild»). */
  loading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      block,
      asChild = false,
      loading = false,
      type,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button'
    const busy = loading && !asChild
    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, size, block, className }))}
        // A button with no type inside a form submits it, by HTML default: almost never
        // the intent of whoever writes <Button onClick>.
        type={asChild ? type : (type ?? 'button')}
        data-surface={variant === 'glass' ? 'ink' : undefined}
        disabled={busy || disabled}
        aria-busy={busy || undefined}
        {...props}
      >
        {busy ? (
          <>
            {/* Decorative: the label beside it is the sentence, and the button
                already carries aria-busy. */}
            <Spinner label="" size="sm" className="text-current" />
            {children}
          </>
        ) : (
          children
        )}
      </Component>
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
